import { useState, useEffect, useMemo } from "react";
import { Trans, useLingui } from "@lingui/react/macro";
import { useNavigate } from "react-router";
import { useAuth } from "react-oidc-context";
import {
  BookOpen,
  GraduationCap,
  Clock,
  CreditCard,
  MapPin,
  Euro,
  PartyPopper,
  X,
} from "lucide-react";
import { toast } from "react-hot-toast";

import Modal from "../components/util/Modal";
import CenteredParagraph from "../components/util/CenteredParagraph";
import ActionButton from "../components/ActionButton";
import Container from "../components/Container";
import GradientHeader from "../components/util/GradientHeader";
import LocationForm, {
  LocationFormData,
} from "../components/location/LocationForm";
import TimeslotForm from "../components/schedule/TimeslotForm";
import TimeslotList from "../components/schedule/TimeslotList";
import {
  useUserDataQuery,
  useBecomeTeacherQuery,
  usePostUserQuery,
} from "../api/userApi";
import {
  useLocationMutation,
  LocationRequest,
  LocationWithName,
  useLocationsQuery,
} from "../api/locationApi";
import {
  useTeacherLessonPricesMutation,
  TeacherLessonPrice,
  TeacherLessonPriceType,
  TeacherLessonPricesDto,
  useNeededActionsQuery,
} from "../api/teacherApi";
import { Tabs } from "../components/Tabs";
import Infobox from "../components/util/InfoBox";
import {
  useSchedule,
  useScheduleMutation,
  Schedule,
  TimeSlot,
} from "../api/scheduleApi";
import { useBecomeTeacherStore } from "../stores/becomeTeacherStore.ts";
import { locales, findAndActivateLocale } from "../i18n.ts";
import { useLanguageStore } from "../stores/languageStore.ts";
import LanguageSelector from "../components/LanguagesSelector.tsx";

type WizardStep =
  | "CHOICE"
  | "TEACH_STRIPE"
  | "TEACH_LOCATION"
  | "TEACH_SCHEDULE"
  | "TEACH_PRICES"
  | "TEACH_COMPLETED";

function FirstSetupWizard() {
  const { user, isAuthenticated } = useAuth();
  const { t } = useLingui();
  const navigate = useNavigate();
  const { language, setLocale } = useLanguageStore();
  const { data: userData, refetch: refetchUserData } = useUserDataQuery(
    user?.profile.sub,
    {
      enabled: isAuthenticated,
    },
  );
  const { data: neededActionsData } = useNeededActionsQuery();
  const { data: locationsData } = useLocationsQuery();

  const [step, setStep] = useState<WizardStep>("CHOICE");
  const [isOpen, setIsOpen] = useState(false);
  const [createdLocation, setCreatedLocation] =
    useState<LocationWithName | null>(null);
  const [isRedirecting, setIsRedirecting] = useState(false);

  const { mutate: becomeTeacher } = useBecomeTeacherQuery();
  const { mutate: postLocation, isPending: isLocationSubmitting } =
    useLocationMutation();
  const { mutate: putPrices, isPending: isPricesSaving } =
    useTeacherLessonPricesMutation();
  const { mutate: updateUser } = usePostUserQuery();

  const { data: schedules, refetch: refetchSchedule } = useSchedule();
  const { mutate: scheduleUpdateMutation } = useScheduleMutation();
  const { isBecomingTeacher, setIsBecomingTeacher } = useBecomeTeacherStore();
  const [isAddingSchedule, setIsAddingSchedule] = useState(false);
  const [deletingScheduleId, setDeletingScheduleId] = useState<string | null>(
    null,
  );

  const timeslots: TimeSlot[] = useMemo(() => {
    const allTimeSlots = schedules
      ?.filter((s) => !createdLocation || s.locationId === createdLocation.id)
      ?.flatMap(({ timeSlots }) => timeSlots);
    return allTimeSlots ? allTimeSlots : [];
  }, [schedules, createdLocation]);

  // Price state
  const [bookingPrices, setBookingPrices] = useState<Record<number, number>>({
    30: 0,
    45: 0,
    60: 0,
  });
  const [subscriptionPrices, setSubscriptionPrices] = useState<
    Record<number, number>
  >({
    30: 0,
    45: 0,
    60: 0,
  });

  useEffect(() => {
    const actions = neededActionsData?.teacherActions || [];
    if (!isAuthenticated || !userData || !user?.profile.sub) return;
    if (userData.teacherStatus && (!neededActionsData || !locationsData))
      return;

    if (userData.setupWizardDone && !isBecomingTeacher) {
      setIsOpen(false);
      return;
    }

    if (isOpen) return;

    if (userData.teacherStatus) {
      const hasLocationAction = actions.some((a) => a.id === "create.location");
      const hasPriceAction = actions.some(
        (a) => a.id === "create.lesson.prices",
      );

      if (hasLocationAction) {
        if (locationsData && locationsData.length > 0) {
          setCreatedLocation(locationsData[0]);
          setStep("TEACH_SCHEDULE");
        } else {
          setStep("TEACH_LOCATION");
        }
      } else if (hasPriceAction) {
        setStep("TEACH_PRICES");
      }
    } else if (isBecomingTeacher) {
      setStep("TEACH_STRIPE");
    } else {
      setStep("CHOICE");
    }
    setIsOpen(true);
  }, [
    isAuthenticated,
    userData,
    neededActionsData,
    locationsData,
    user?.profile.sub,
    isOpen,
    isBecomingTeacher,
  ]);

  function handleLearn() {
    markSetupCompleted();
    navigate("/");
  }

  function handleTeach() {
    setStep("TEACH_STRIPE");
  }

  function markSetupCompleted() {
    if (user && userData) {
      updateUser(
        { ...userData, setupWizardDone: true },
        {
          onSuccess: () => {
            refetchUserData().then(() => {
              setIsBecomingTeacher(false);
              setIsOpen(false);
            });
          },
          onError: () => {
            toast.error(
              t`Could not mark setup as completed. Please try again later.`,
            );
          },
        },
      );
    }
  }

  function handleBecomeTeacher() {
    setIsRedirecting(true);
    becomeTeacher(undefined, {
      onSuccess: (onboardingResponse) => {
        window.location.replace(onboardingResponse.redirectUrl);
      },
      onError: () => {
        toast.error(
          t`Could not start teacher onboarding. Please try again later.`,
        );
      },
    });
  }

  const handleLocationSubmit = (formData: LocationFormData) => {
    const locationRequest: LocationRequest = {
      name: formData.name,
      locationType: formData.locationType,
      instruments: formData.instruments,
      longitude: formData.selectedAddress?.longitude,
      latitude: formData.selectedAddress?.latitude,
      countryCode: formData.selectedAddress?.countryCode,
      postalCode: formData.selectedAddress?.postalCode,
      city: formData.selectedAddress?.city,
      streetName: formData.selectedAddress?.streetName,
      houseNumber: formData.selectedAddress?.houseNumber,
      region: formData.selectedAddress?.region,
      buildingName: formData.selectedAddress?.buildingName,
      subBuildingName: formData.selectedAddress?.subBuildingName,
      timeZone:
        formData.selectedAddress?.timeZone ||
        Intl.DateTimeFormat().resolvedOptions().timeZone,
    };

    postLocation(locationRequest, {
      onSuccess: (data) => {
        toast.success(t`Location successfully created!`);
        setCreatedLocation(data as LocationWithName);
        setStep("TEACH_SCHEDULE");
      },
      onError: () => {
        toast.error(t`Failed to create location. Please try again.`);
      },
    });
  };

  const handleScheduleAdd = (newTimeSlot: TimeSlot) => {
    setIsAddingSchedule(true);
    const schedule = schedules?.find(
      ({ locationId }) => locationId === newTimeSlot.locationId,
    );
    const updatedSchedule: Schedule = {
      id: schedule?.id,
      locationId: newTimeSlot.locationId,
      timeSlots: [...(schedule?.timeSlots || []), newTimeSlot],
    };
    scheduleUpdateMutation(updatedSchedule, {
      onSettled: () => {
        refetchSchedule()
          .catch(console.error)
          .finally(() => setIsAddingSchedule(false));
      },
    });
  };

  const handleScheduleDelete = (updateLocationId: string, id: string) => {
    setDeletingScheduleId(id);
    const schedule = schedules?.find(
      ({ locationId }) => locationId === updateLocationId,
    );
    const timeSlots =
      schedule?.timeSlots.filter((timeSlot) => timeSlot.id !== id) || [];
    scheduleUpdateMutation(
      {
        id: schedule!.id,
        locationId: schedule!.locationId,
        timeSlots: timeSlots,
      },
      {
        onSettled: () => {
          refetchSchedule()
            .catch(console.error)
            .finally(() => setDeletingScheduleId(null));
        },
      },
    );
  };

  const handlePricesSave = () => {
    const durations = [30, 45, 60];
    const lessonPrices: TeacherLessonPrice[] = [
      ...durations.map((d) => ({
        durationInMinutes: d,
        price: bookingPrices[d] || 0,
        currency: "EUR",
        type: "BOOKING" as TeacherLessonPriceType,
      })),
      ...durations.map((d) => ({
        durationInMinutes: d,
        price: subscriptionPrices[d] || 0,
        currency: "EUR",
        type: "SUBSCRIPTION" as TeacherLessonPriceType,
      })),
    ];

    const hasAnyPrice = lessonPrices.some((lp) => lp.price > 0);
    if (!hasAnyPrice) {
      toast.error(t`Please add at least one lesson price.`);
      return;
    }

    const payload: TeacherLessonPricesDto = {
      prices: lessonPrices,
    };

    putPrices(payload, {
      onSuccess: () => {
        toast.success(t`Lesson Prices updated successfully.`);
        setStep("TEACH_COMPLETED");
      },
      onError: () => {
        toast.error(t`Failed to update lesson prices.`);
      },
    });
  };

  const finishTeacherSetup = () => {
    markSetupCompleted();
  };

  const handlePriceChange = (
    type: TeacherLessonPriceType,
    duration: number,
    value: string,
  ) => {
    const numericValue = parseFloat(value) || 0;
    if (type === "BOOKING") {
      setBookingPrices((prev) => ({ ...prev, [duration]: numericValue }));
    } else {
      setSubscriptionPrices((prev) => ({ ...prev, [duration]: numericValue }));
    }
  };

  if (!isOpen || !userData) return null;

  const handleWizardLanguageChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    findAndActivateLocale(event.target.value)
      .then(setLocale)
      .catch(console.error);
  };

  const renderChoiceStep = () => (
    <>
      <div className="mb-4 flex justify-center">
        <LanguageSelector
          locales={locales}
          locale={language}
          handleLanguageChange={handleWizardLanguageChange}
          showLabel
        />
      </div>

      <GradientHeader className="text-center">
        <Trans>Welcome to Soluud!</Trans>
      </GradientHeader>

      <CenteredParagraph>
        <Trans>Soluud is your platform for learning and teaching music.</Trans>
      </CenteredParagraph>
      <CenteredParagraph className="text-center">
        <Trans>Do you want to learn or teach?</Trans>
      </CenteredParagraph>
      <CenteredParagraph className="mt-6 flex justify-center gap-x-4">
        <ActionButton
          onClick={handleLearn}
          variant="secondary"
          className="flex items-center gap-2 px-6 py-3"
        >
          <BookOpen size={20} />
          <Trans>Learn</Trans>
        </ActionButton>
        <ActionButton
          onClick={handleTeach}
          variant="primary"
          className="flex items-center gap-2 px-6 py-3"
        >
          <GraduationCap size={20} />
          <Trans>Teach</Trans>
        </ActionButton>
      </CenteredParagraph>
    </>
  );

  const renderStripeStep = () => (
    <div className="flex flex-col items-center text-center">
      <CreditCard className="text-soluud-primary mb-4 h-12 w-12" />
      <GradientHeader>
        <Trans>Setup Payouts</Trans>
      </GradientHeader>
      <CenteredParagraph>
        <Trans>
          To receive payments from your students, we use Stripe. You'll be
          redirected to Stripe to create or connect your account.
        </Trans>
      </CenteredParagraph>
      <CenteredParagraph className="mt-2 text-sm text-gray-500">
        <Trans>
          You have to create a fully onboarded stripe account before you can be
          booked as a teacher and we can pay out your earnings.
        </Trans>
      </CenteredParagraph>
      <div className="mt-6 flex gap-4">
        <ActionButton
          variant="secondary"
          onClick={() => {
            if (!isBecomingTeacher) {
              setStep("CHOICE");
            } else {
              setIsBecomingTeacher(false);
              setIsOpen(false);
            }
          }}
        >
          <Trans>Back</Trans>
        </ActionButton>
        <ActionButton onClick={handleBecomeTeacher} isLoading={isRedirecting}>
          <Trans>Connect with Stripe</Trans>
        </ActionButton>
      </div>
    </div>
  );

  const renderLocationStep = () => (
    <div className="w-full">
      <div className="mb-6 flex flex-col items-center text-center">
        <MapPin className="text-soluud-primary mb-2 h-10 w-10" />
        <GradientHeader>
          <Trans>Your First Location</Trans>
        </GradientHeader>
        <CenteredParagraph>
          <Trans>
            Where will you be teaching? Add your first location to get started.
          </Trans>
        </CenteredParagraph>
      </div>
      <div className="mx-auto max-w-lg">
        <LocationForm
          initialData={{
            name: "",
            instruments: [],
            locationType: "IN_PERSON",
            selectedAddress: null,
          }}
          onSubmit={handleLocationSubmit}
          isSubmitting={isLocationSubmitting}
          submitLabel={t`Next Step`}
        />
      </div>
    </div>
  );

  const renderScheduleStep = () => {
    if (!createdLocation) return null;

    return (
      <div className="w-full">
        <div className="mb-6 flex flex-col items-center text-center">
          <Clock className="text-soluud-primary mb-2 h-10 w-10" />
          <GradientHeader>
            <Trans>Your Teaching Schedule</Trans>
          </GradientHeader>
          <CenteredParagraph>
            <Trans>
              When are you available for lessons at {createdLocation.name}? Add
              your regular weekly availability.
            </Trans>
          </CenteredParagraph>
        </div>
        <div className="mx-auto max-w-2xl">
          <TimeslotForm
            onAddTimeslot={handleScheduleAdd}
            existingTimeslots={timeslots}
            isLoading={isAddingSchedule}
            locations={[createdLocation]}
            onSelectLocation={() => {}}
            selectedLocation={createdLocation}
            hideLocation
          />
          <TimeslotList
            timeslots={timeslots}
            onDeleteTimeslot={handleScheduleDelete}
            deletingId={deletingScheduleId}
            locations={[createdLocation]}
            hideLocation
          />
          <div className="mt-8 flex justify-center">
            <ActionButton
              onClick={() => {
                if (timeslots.length === 0) {
                  toast.error(
                    t`Please add at least one availability timeslot.`,
                  );
                  return;
                }
                setStep("TEACH_PRICES");
              }}
              className="px-12"
            >
              <Trans>Next Step</Trans>
            </ActionButton>
          </div>
        </div>
      </div>
    );
  };

  const renderPricesStep = () => (
    <div className="w-full">
      <div className="mb-6 flex flex-col items-center text-center">
        <Euro className="text-soluud-primary mb-2 h-10 w-10" />
        <GradientHeader>
          <Trans>Set Your Prices</Trans>
        </GradientHeader>
        <CenteredParagraph>
          <Trans>How much would you like to charge for your lessons?</Trans>
        </CenteredParagraph>
      </div>

      <Tabs defaultValue="booking">
        <Tabs.Tab label={<Trans>Single Booking</Trans>} value="booking">
          <div className="flex flex-col gap-4">
            <Infobox>
              <Trans>
                Set prices for individual lesson bookings. Students can book a
                single lesson at these rates. Not filling in a price will not
                allow the user to select the duration.
              </Trans>
            </Infobox>
            <div className="flex flex-col divide-y divide-gray-100">
              {[30, 45, 60].map((duration) => {
                const hasPrice = bookingPrices[duration] > 0;
                return (
                  <div
                    key={duration}
                    className="flex items-center gap-4 py-3.5 first:pt-0 last:pb-0"
                  >
                    <div className="flex min-w-27.5 items-center gap-2">
                      <Clock className="text-soluud-primary h-4 w-4 shrink-0" />
                      <span className="text-soluud-text-primary text-sm font-medium">
                        {duration} <Trans>min</Trans>
                      </span>
                    </div>
                    <div className="relative flex-1">
                      <span className="text-soluud-text-tertiary pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm select-none">
                        €
                      </span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={bookingPrices[duration] || ""}
                        onChange={(e) =>
                          handlePriceChange("BOOKING", duration, e.target.value)
                        }
                        placeholder="0.00"
                        className="focus:border-soluud-primary focus:ring-soluud-primary/30 w-full rounded-lg border border-gray-300 py-2 pr-3 pl-7 text-sm transition-colors duration-150 focus:ring-2 focus:outline-none"
                      />
                    </div>
                    <span
                      className={`hidden w-24 shrink-0 text-right text-xs sm:block ${hasPrice ? "text-soluud-primary font-medium" : "text-soluud-text-tertiary"}`}
                    >
                      {hasPrice ? (
                        <Trans>Available</Trans>
                      ) : (
                        <Trans>Unavailable</Trans>
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </Tabs.Tab>
        <Tabs.Tab label={<Trans>Subscription</Trans>} value="subscription">
          <div className="flex flex-col gap-4">
            <Infobox>
              <Trans>
                Set monthly subscription prices for regular lessons. Students
                will be charged on a recurring basis. Not filling in a price
                will not allow the user to select the duration.
              </Trans>
            </Infobox>
            <div className="flex flex-col divide-y divide-gray-100">
              {[30, 45, 60].map((duration) => {
                const hasPrice = subscriptionPrices[duration] > 0;
                return (
                  <div
                    key={duration}
                    className="flex items-center gap-4 py-3.5 first:pt-0 last:pb-0"
                  >
                    <div className="flex min-w-27.5 items-center gap-2">
                      <Clock className="text-soluud-primary h-4 w-4 shrink-0" />
                      <span className="text-soluud-text-primary text-sm font-medium">
                        {duration} <Trans>min</Trans>
                      </span>
                    </div>
                    <div className="relative flex-1">
                      <span className="text-soluud-text-tertiary pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm select-none">
                        €
                      </span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={subscriptionPrices[duration] || ""}
                        onChange={(e) =>
                          handlePriceChange(
                            "SUBSCRIPTION",
                            duration,
                            e.target.value,
                          )
                        }
                        placeholder="0.00"
                        className="focus:border-soluud-primary focus:ring-soluud-primary/30 w-full rounded-lg border border-gray-300 py-2 pr-3 pl-7 text-sm transition-colors duration-150 focus:ring-2 focus:outline-none"
                      />
                    </div>
                    <span
                      className={`hidden w-24 shrink-0 text-right text-xs sm:block ${hasPrice ? "text-soluud-primary font-medium" : "text-soluud-text-tertiary"}`}
                    >
                      {hasPrice ? (
                        <Trans>Available</Trans>
                      ) : (
                        <Trans>Unavailable</Trans>
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </Tabs.Tab>
      </Tabs>

      <div className="mt-8 flex justify-center">
        <ActionButton
          onClick={handlePricesSave}
          isLoading={isPricesSaving}
          className="px-12"
        >
          <Trans>Next Step</Trans>
        </ActionButton>
      </div>
    </div>
  );

  const renderCompletedStep = () => (
    <div className="w-full">
      <div className="mb-6 flex flex-col items-center text-center">
        <PartyPopper className="text-soluud-primary mb-2 h-10 w-10" />
        <GradientHeader>
          <Trans>Welcome in our teacher community!</Trans>
        </GradientHeader>
        <CenteredParagraph>
          <Trans>
            You finished your teaching setup! Now you can be found by our users.
            Wait for a notification with your first booking!
          </Trans>
          <div className="mt-8 flex justify-center">
            <ActionButton onClick={finishTeacherSetup} className="px-12">
              <Trans>Finish Setup</Trans>
            </ActionButton>
          </div>
        </CenteredParagraph>
      </div>
    </div>
  );

  return (
    <Modal
      show={isOpen}
      className="flex max-h-full flex-col md:max-w-2xl md:min-w-2xl md:px-0"
    >
      <Container className="relative max-h-full overflow-y-auto py-8">
        <button
          type="button"
          onClick={markSetupCompleted}
          className="hover:bg-soluud-background absolute top-4 right-4 cursor-pointer rounded-full p-2 text-gray-400 transition-colors hover:text-gray-600"
          aria-label={t`Close wizard`}
        >
          <X size={20} />
        </button>
        {step === "CHOICE" && renderChoiceStep()}
        {step === "TEACH_STRIPE" && renderStripeStep()}
        {step === "TEACH_LOCATION" && renderLocationStep()}
        {step === "TEACH_SCHEDULE" && renderScheduleStep()}
        {step === "TEACH_PRICES" && renderPricesStep()}
        {step === "TEACH_COMPLETED" && renderCompletedStep()}
      </Container>
    </Modal>
  );
}

export default FirstSetupWizard;
